import {
  AccountLimitedResponse,
  ApiError,
  ParentLimitedResponse,
  StudentLimitedResponse,
  TasksService,
  TutorPublicResponse,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { DialogClose, DialogContent } from "components/Dialog";
import { ReactNode, useContext, useRef, useState } from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { TaskPerson, taskPeople, toRecipients } from "./PeoplePicker";
import TaskFields, { TaskDraft } from "./TaskFields";
import "./create.css";

/**
 * The New task form: its fields and Create task. The New task dialog and
 * the CRM board card's pop-out both use it. A save calls onCreated; the form
 * starts empty again when it is shown again.
 */
export function NewTaskForm({
  students,
  parents,
  tutors,
  admins,
  initialTask,
  suggested = [],
  idPrefix = "task",
  dateContainer,
  fitMenusToWindow = false,
  cancel,
  onCreated,
}: {
  students?: StudentLimitedResponse[];
  parents?: ParentLimitedResponse[];
  tutors?: TutorPublicResponse[];
  admins?: AccountLimitedResponse[];
  /** Starting values, e.g. a person already tagged (the People panel's add task). */
  initialTask?: Partial<TaskDraft>;
  /** People in context, listed first in the pickers (a person and their family). */
  suggested?: TaskPerson[];
  /** Makes the fields' ids unique on the page. */
  idPrefix?: string;
  /** Where the date picker's calendar opens. */
  dateContainer: () => HTMLElement;
  /** In a pop-out: the pickers' menus fit the window (PeoplePicker). */
  fitMenusToWindow?: boolean;
  /** Shown left of Create task. */
  cancel?: ReactNode;
  onCreated: () => void;
}) {
  const [task, setTask] = useState<TaskDraft>(() => ({
    name: "",
    content: "",
    due_date: new Date().toISOString(),
    assigned: [],
    tagged: [],
    ...initialTask,
  }));
  const [isCreating, setIsCreating] = useState(false);

  const { account, currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );

  // Everyone a task can name, for both pickers. The signed-in admin is
  // suggested first for Assign to, the people in context first for About.
  const people = taskPeople({ admins, tutors, parents, students });
  const me: TaskPerson[] = account
    ? [
        {
          role: "admin",
          id: account.reference_id,
          label: `${account.first_name} ${account.last_name}`,
        },
      ]
    : [];

  const handleCreate = () => {
    if (!task.name.trim() || !task.due_date || task.assigned.length === 0) {
      Notifications.error(
        "Add a task name, a due date and at least one person to assign it to."
      );
      return;
    }

    setIsCreating(true);
    TasksService.createTask({
      requestBody: {
        name: task.name,
        content: task.content,
        due_date: task.due_date,
        org_id: currently_selected_organization as number,
        assigned_users: toRecipients(task.assigned),
        tagged_users: toRecipients(task.tagged),
      },
    })
      .then(() => {
        Notifications.success("Task created successfully");
        onCreated();
      })
      .catch((err: ApiError) => {
        Notifications.error(
          err.body?.detail || "An unexpected error occurred."
        );
      })
      .finally(() => setIsCreating(false));
  };

  return (
    <>
      <TaskFields
        idPrefix={idPrefix}
        task={task}
        onChange={setTask}
        people={people}
        suggestedAssign={[...me, ...suggested]}
        suggestedAbout={[...suggested, ...me]}
        dateContainer={dateContainer}
        fitMenusToWindow={fitMenusToWindow}
      />

      <div className="task-form__actions">
        {cancel}
        <Button
          size={ButtonSize.SMALL}
          color={ButtonColor.GREEN}
          onClick={handleCreate}
          disabled={isCreating}
        >
          {isCreating ? "Creating…" : "Create task"}
        </Button>
      </div>
    </>
  );
}

function CreateTaskDialog({
  students,
  parents,
  tutors,
  admins,
  onClose,
  refreshTasks,
  initialTask,
  suggested = [],
}: {
  students?: StudentLimitedResponse[];
  parents?: ParentLimitedResponse[];
  tutors?: TutorPublicResponse[];
  admins?: AccountLimitedResponse[];
  onClose: () => void;
  refreshTasks: () => void;
  /** Starting values, e.g. a person already tagged (the People panel's add task). */
  initialTask?: Partial<TaskDraft>;
  /** People in context, listed first in the pickers (a person and their family). */
  suggested?: TaskPerson[];
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // The form is inside the dialog's content, so closing resets it.
  return (
    <DialogContent
      ref={dialogRef}
      className="dialog-content task-form"
      onClose={onClose}
    >
      <h2 className="task-form__title">New task</h2>

      <NewTaskForm
        students={students}
        parents={parents}
        tutors={tutors}
        admins={admins}
        initialTask={initialTask}
        suggested={suggested}
        // Inside the dialog, so the calendar takes clicks.
        dateContainer={() => dialogRef.current || document.body}
        cancel={
          <DialogClose asChild>
            <Button
              ref={cancelRef}
              size={ButtonSize.SMALL}
              fill={ButtonFill.HOLLOW}
              color={ButtonColor.GREEN}
            >
              Cancel
            </Button>
          </DialogClose>
        }
        onCreated={() => {
          refreshTasks();
          cancelRef.current?.click();
        }}
      />
    </DialogContent>
  );
}

export default CreateTaskDialog;
